import type { BillingPurchaseAttempt, BillingPurchaseOptions } from '$lib/api/types.gen';

import { BUYING_UNAVAILABLE, sharedBillingErrorMessage } from './billing-messages';

/** Query parameter that opens the buy dialog on a page that offers it (e.g. `/usage?buy`). */
export const BUY_DIALOG_PARAM = 'buy';

export type PurchaseAttemptStatus = BillingPurchaseAttempt['status'];

/** Options as served while purchases are on; every field the UI computes with is non-null. */
export type EnabledPurchaseOptions = BillingPurchaseOptions & {
	enabled: true;
	unit_price_micro_usd: number;
	min_dream_bucks: string;
	max_dream_bucks: string;
};

export function enabledPurchaseOptions(
	options: BillingPurchaseOptions | null | undefined,
): EnabledPurchaseOptions | undefined {
	if (
		!options?.enabled ||
		typeof options.unit_price_micro_usd !== 'number' ||
		options.unit_price_micro_usd <= 0 ||
		!isWholeNumber(options.min_dream_bucks) ||
		!isWholeNumber(options.max_dream_bucks)
	) {
		return undefined;
	}
	return options as EnabledPurchaseOptions;
}

function isWholeNumber(value: string | null | undefined): value is string {
	return typeof value === 'string' && /^[1-9][0-9]*$/.test(value);
}

/**
 * Parse what the user typed into a whole number of Dream Bucks, or `undefined` when it is not
 * one. Rejects fractions, signs, exponents and leading zeros, since the API takes `^[1-9][0-9]*$`.
 */
export function parsePurchaseAmount(input: string): string | undefined {
	const trimmed = input.trim().replaceAll(',', '');
	return isWholeNumber(trimmed) ? trimmed : undefined;
}

export type PurchaseAmountProblem = 'invalid' | 'too_small' | 'too_large';

export function purchaseAmountProblem(
	input: string,
	options: Pick<EnabledPurchaseOptions, 'min_dream_bucks' | 'max_dream_bucks'>,
): PurchaseAmountProblem | undefined {
	const amount = parsePurchaseAmount(input);
	if (amount === undefined) return 'invalid';
	if (BigInt(amount) < BigInt(options.min_dream_bucks)) return 'too_small';
	if (BigInt(amount) > BigInt(options.max_dream_bucks)) return 'too_large';
	return undefined;
}

/** Price of `dreamBucks` before tax in micro-USD, computed from the served unit price. */
export function purchasePriceMicroUsd(dreamBucks: string, unitPriceMicroUsd: number): bigint {
	return BigInt(dreamBucks) * BigInt(unitPriceMicroUsd);
}

const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

/** Format micro-USD as dollars, rounded half up to the cent (Stripe charges whole cents). */
export function formatMicroUsd(micro: bigint): string {
	const cents = (micro + 5_000n) / 10_000n;
	return usd.format(Number(cents) / 100);
}

export function formatPurchasePrice(dreamBucks: string, unitPriceMicroUsd: number): string {
	return formatMicroUsd(purchasePriceMicroUsd(dreamBucks, unitPriceMicroUsd));
}

export function purchaseAmountMessage(
	problem: PurchaseAmountProblem,
	options: Pick<EnabledPurchaseOptions, 'min_dream_bucks' | 'max_dream_bucks'>,
): string {
	return problem === 'invalid'
		? 'Enter a whole number of Dream Bucks.'
		: `Enter between ${Number(options.min_dream_bucks).toLocaleString('en-US')} and ${Number(options.max_dream_bucks).toLocaleString('en-US')} Dream Bucks.`;
}

/** What the buy dialog says when there is nothing to buy, as the user's own role or the server allows. */
export function purchaseUnavailableMessage(reason: 'not_allowed' | 'unavailable'): string {
	return reason === 'not_allowed'
		? 'You don’t have permission to buy Dream Bucks for this organization. Ask an owner.'
		: BUYING_UNAVAILABLE;
}

/** User-facing text for a refused purchase, by the API's error `kind`. */
export function purchaseErrorMessage(kind: string | undefined, fallback: string): string {
	const shared = sharedBillingErrorMessage(kind);
	if (shared) return shared;
	switch (kind) {
		case 'PURCHASES_DISABLED':
			return BUYING_UNAVAILABLE;
		case 'PURCHASE_ATTEMPT_NOT_OPEN':
			return 'That checkout is no longer open. Try again.';
		default:
			return fallback || 'The purchase couldn’t be started.';
	}
}

/** Poll quickly while a card payment is most likely to land, then ease off. */
export const PURCHASE_FAST_POLL_INTERVAL_MS = 500;
export const PURCHASE_FAST_POLL_DURATION_MS = 30_000;
export const PURCHASE_POLL_INTERVAL_MS = 2_000;

export function purchasePollDelay(elapsedMs: number): number {
	return elapsedMs < PURCHASE_FAST_POLL_DURATION_MS
		? PURCHASE_FAST_POLL_INTERVAL_MS
		: PURCHASE_POLL_INTERVAL_MS;
}
export const PURCHASE_PENDING_TIMEOUT_MS = 3 * 60_000;

/**
 * The watched attempt's state. Card setup attempts share the purchase lifecycle (minus `failed`),
 * so the same watcher serves both; `A` defaults to a purchase attempt.
 */
export type PurchaseWatchState<A extends { status: string } = BillingPurchaseAttempt> =
	| { kind: 'waiting'; status: 'open' | 'pending' | 'unknown' }
	| { kind: 'succeeded'; attempt: A }
	| { kind: 'expired'; attempt: A }
	| { kind: 'failed'; attempt: A }
	/** The API doesn't know the attempt (a mistyped or stale link), so there is nothing to wait for. */
	| { kind: 'not_found' }
	/** Polling stopped while still `open` or `pending`; re-check when the user returns. */
	| {
			kind: 'slow';
			status: 'open' | 'pending' | 'unknown';
			attempt: A | undefined;
	  };

/**
 * Map an attempt to a watch state. The status is an open enum, so anything unrecognised waits
 * like `pending` (§5 of the contract) instead of being shown as a failure.
 */
export function purchaseWatchState<A extends { status: string } = BillingPurchaseAttempt>(
	attempt: A,
): PurchaseWatchState<A> {
	switch (attempt.status) {
		case 'succeeded':
			return { kind: 'succeeded', attempt };
		case 'expired':
			return { kind: 'expired', attempt };
		case 'failed':
			return { kind: 'failed', attempt };
		case 'open':
		case 'pending':
			return { kind: 'waiting', status: attempt.status };
		default:
			return { kind: 'waiting', status: 'unknown' };
	}
}

export function isFinalPurchaseState(state: PurchaseWatchState<{ status: string }>) {
	return (
		state.kind === 'succeeded' ||
		state.kind === 'expired' ||
		state.kind === 'failed' ||
		state.kind === 'not_found'
	);
}

/**
 * Poll an attempt (see `purchasePollDelay`, or a fixed `intervalMs`) until it is final, or until it has stayed `open`/`pending` for
 * `pendingTimeoutMs` (a delayed payment method can stay `pending` for days). A failed poll keeps
 * the last known state and tries again; the API interceptor reports the error.
 * Returns a function that stops the watch.
 */
export function watchPurchaseAttempt<A extends { status: string } = BillingPurchaseAttempt>(
	/** The attempt; `undefined` when it can't be read (yet); `'not_found'` when the API doesn't know it. */
	loadAttempt: () => Promise<A | 'not_found' | undefined>,
	onState: (state: PurchaseWatchState<A>) => void,
	{
		intervalMs,
		pendingTimeoutMs = PURCHASE_PENDING_TIMEOUT_MS,
		now = Date.now,
	}: { intervalMs?: number; pendingTimeoutMs?: number; now?: () => number } = {},
) {
	const startedAt = now();
	let stopped = false;
	let timer: ReturnType<typeof setTimeout> | undefined;
	let last: A | undefined;

	async function poll() {
		let attempt: A | 'not_found' | undefined;
		try {
			attempt = await loadAttempt();
		} catch {
			// Transient: keep polling with whatever we last knew.
		}
		if (stopped) return;
		if (attempt === 'not_found') {
			onState({ kind: 'not_found' });
			return;
		}
		if (attempt) last = attempt;
		const state: PurchaseWatchState<A> = last
			? purchaseWatchState(last)
			: { kind: 'waiting', status: 'unknown' };
		if (isFinalPurchaseState(state)) {
			onState(state);
			return;
		}
		if (now() - startedAt >= pendingTimeoutMs && state.kind === 'waiting') {
			onState({ kind: 'slow', status: state.status, attempt: last });
			return;
		}
		onState(state);
		timer = setTimeout(() => void poll(), intervalMs ?? purchasePollDelay(now() - startedAt));
	}

	void poll();
	return () => {
		stopped = true;
		clearTimeout(timer);
	};
}
